/**
 * @fileoverview Contact information section for the footer with animated links and navigation.
 * Features hover animations, contact details, navigation menu, and social media links.
 */

/**
 * Props interface for animated text links.
 */
interface AnimatedLinkProps {
  href?: string;
  onClick?: () => void;
  children: string;
  className?: string;
  target?: string;
  rel?: string;
}

/**
 * Reusable animated link component with sliding text effect.
 *
 * @param {AnimatedLinkProps} props - Component properties
 * @returns {JSX.Element} Animated link element
 */
const AnimatedLink: React.FC<AnimatedLinkProps> = ({
  href,
  onClick,
  children,
  className = '',
  target,
  rel,
}) => {
  /**
   * Handle hover animation for sliding text effect.
   */
  const handleHover = (
    e: React.MouseEvent<HTMLElement>,
    isEntering: boolean,
  ): void => {
    const target = e.currentTarget;
    const topSpan = target.querySelector('.top-text') as HTMLElement;
    const bottomSpan = target.querySelector('.bottom-text') as HTMLElement;

    if (topSpan && bottomSpan) {
      if (isEntering) {
        topSpan.style.transform = 'translateY(-100%)';
        topSpan.style.opacity = '0';
        bottomSpan.style.transform = 'translateY(0%)';
        bottomSpan.style.opacity = '1';
      } else {
        topSpan.style.transform = 'translateY(0%)';
        topSpan.style.opacity = '1';
        bottomSpan.style.transform = 'translateY(100%)';
        bottomSpan.style.opacity = '0';
      }
    }
  };

  const Element = onClick ? 'button' : 'a';
  const elementProps = onClick
    ? {
        onClick,
        className: `${className} bg-transparent border-none cursor-pointer`,
      }
    : { href, target, rel, className };

  return (
    <Element
      {...elementProps}
      className={`relative overflow-hidden inline-block ${className}`}
      onMouseEnter={(e) => handleHover(e, true)}
      onMouseLeave={(e) => handleHover(e, false)}
    >
      <span
        className="top-text inline-block transition-all duration-150 ease-linear"
        style={{ transform: 'translateY(0%)', opacity: 1 }}
      >
        {children}
      </span>
      <span
        className="bottom-text absolute left-0 top-0 inline-block transition-all duration-150 ease-linear"
        style={{ transform: 'translateY(100%)', opacity: 0 }}
      >
        {children}
      </span>
    </Element>
  );
};

/**
 * Contact information component with animated interactive elements.
 *
 * @description Renders the main contact section of the footer including email, phone,
 * navigation menu, and social media links. Features smooth scroll navigation and
 * hover animations with sliding text effects.
 *
 * @returns {JSX.Element} Complete contact information section
 */
export default function ContactInfo() {
  /**
   * Smooth scroll to page sections utility function.
   */
  const scrollToSection = (sectionId: string): void => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  /** Navigation menu items */
  const navigationItems = [
    { section: '', label: 'Inicio', isHome: true },
    { section: 'nosotros', label: 'Nosotros', isHome: false },
    { section: 'proyectos', label: 'Proyectos', isHome: false },
    { section: 'servicios', label: 'Servicios', isHome: false },
  ];

  /** Social media links */
  const socialLinks = [
    { href: 'https://www.instagram.com/_ghostcvlt_/', label: 'Instagram' },
    { href: 'https://www.facebook.com/TheGhostCvlt', label: 'Facebook' },
  ];

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 min-h-[40vh] lg:min-h-[45vh]">
        {/* Contact information */}
        <div className="lg:col-span-7 space-y-6 lg:space-y-12">
          <div>
            <div>
              <p className="text-xs font-urbanist font-medium uppercase tracking-[0.2em] text-white/50 mb-2 lg:mb-3">
                Email
              </p>
              <AnimatedLink
                href="mailto:contacto@ghostcvlt.com"
                className="text-[5vw] lg:text-4xl xl:text-5xl font-urbanist text-white lowercase tracking-normal leading-tight"
              >
                contacto@ghostcvlt.com
              </AnimatedLink>
            </div>
          </div>

          <div>
            <div>
              <p className="text-xs font-urbanist font-medium uppercase tracking-[0.2em] text-white/50 mb-2 lg:mb-3">
                Teléfono
              </p>
              <AnimatedLink
                href="tel:+5215512388872"
                className="text-[4vw] lg:text-3xl font-urbanist text-white"
              >
                +52 1 55 1238 8872
              </AnimatedLink>
            </div>
          </div>
        </div>

        {/* Navigation and social media */}
        <div className="lg:col-span-5 space-y-6 lg:space-y-10">
          <div>
            <p className="text-xs font-urbanist uppercase tracking-[0.2em] text-white/50 mb-2 lg:mb-3">
              Navegación
            </p>
            <nav className="space-y-2">
              {navigationItems.map((item) => (
                <div key={item.section}>
                  <AnimatedLink
                    onClick={() => {
                      if (item.isHome) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      } else {
                        scrollToSection(item.section);
                      }
                    }}
                    className="text-[4vw] lg:text-xl font-urbanist text-white capitalize"
                  >
                    {item.label}
                  </AnimatedLink>
                </div>
              ))}
            </nav>
          </div>

          <div>
            <p className="text-xs font-urbanist uppercase tracking-[0.2em] text-white/50 mb-2 lg:mb-3">
              Redes sociales
            </p>
            <div className="flex flex-col space-y-2">
              {socialLinks.map((social) => (
                <div key={social.label} className="w-fit">
                  <AnimatedLink
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[4vw] lg:text-xl font-urbanist text-white capitalize"
                  >
                    {social.label}
                  </AnimatedLink>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
